import { useCallback, useEffect, useRef, useState } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import {
    IconDownload,
    IconPrinter,
    IconRefresh,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import { printBlob } from '../../../../../utils/generateDocPdf';
import useImageFileEdits from '../../components/image/useImageFileEdits';
import useImagePaste from '../../components/image/useImagePaste';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import PdfDocPreview from '../../components/pdf/PdfDocPreview';
import ToolCard from '../../components/common/ToolCard';
import ImageGridPreview from '../shared/ImageGridPreview';
import {
    createPdfFromImages,
    defaultPdfName,
    formatFileSize,
    DEFAULT_CREATE_PDF_SETTINGS,
    isSupportedToPdfImageFile,
} from './toPdfImageEngine';
import {
    createToolItemId,
    deriveToolStatus,
    downloadBlob,
    getToolStatusText,
} from '../../toolPageUtils';
import ToPdfImageSettings from './ToPdfImageSettings';

const MAX_FILE_BYTES = 50 * 1024 * 1024;

const ACCEPT = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/gif',
    'image/bmp',
    'image/heic',
    'image/heif',
    'image/avif',
    '.jpg',
    '.jpeg',
    '.png',
    '.webp',
    '.gif',
    '.bmp',
    '.heic',
    '.heif',
    '.avif',
].join(',');

export default function ToPdfImage() {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);

    const [items, setItems] = useState([]);
    const [selectedItemId, setSelectedItemId] = useState(null);
    const selectedItem = items.find((item) => item.id === selectedItemId) ?? null;
    const selectedIndex = items.findIndex((item) => item.id === selectedItemId);
    const [settings, setSettings] = useState(DEFAULT_CREATE_PDF_SETTINGS);
    const [isDragging, setIsDragging] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [progress, setProgress] = useState(null);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [printing, setPrinting] = useState(false);

    useEffect(() => {
        if (!items.length) {
            setSelectedItemId(null);
            return;
        }

        setSelectedItemId((current) => {
            if (current && items.some((item) => item.id === current)) return current;
            return items[0].id;
        });
    }, [items]);

    const addFiles = useCallback((incoming) => {
        const list = Array.from(incoming || []);
        const images = list.filter(isSupportedToPdfImageFile);
        if (!images.length) {
            const msg = t('toPdfImage.errors.invalidType');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        let tooLarge = false;
        setItems((prev) => {
            const next = [...prev];
            for (const file of images) {
                if (file.size > MAX_FILE_BYTES) {
                    tooLarge = true;
                    continue;
                }
                if (next.some((item) => item.file.name === file.name && item.file.size === file.size)) continue;
                next.push({ id: createToolItemId(), file });
            }
            return next;
        });

        if (tooLarge) {
            const msg = t('toPdfImage.errors.tooLarge');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } else {
            setError(null);
        }
        setResult(null);
    }, [t]);

    // Ctrl/Cmd+V anywhere on the page drops a copied picture straight in.
    useImagePaste(addFiles);

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        addFiles(e.dataTransfer.files);
    };

    const removeItem = (id) => {
        setItems((prev) => prev.filter((item) => item.id !== id));
        setResult(null);
        setError(null);
    };

    // Crop / rotate / duplicate / delete, shared with every other image tool.
    // Items carry an id for drag ordering, so the edits map onto that list.
    const { actions: fileActions, cropDialog } = useImageFileEdits({
        files: items.map((item) => item.file),
        onReplace: (index, file) => {
            setItems((prev) => prev.map((item, i) => (i === index ? { ...item, file } : item)));
            setResult(null);
            setError(null);
        },
        onInsert: (index, file) => {
            setItems((prev) => {
                const next = [...prev];
                next.splice(index + 1, 0, { id: createToolItemId(), file });
                return next;
            });
            setResult(null);
            setError(null);
        },
        onRemove: (index) => {
            const item = items[index];
            if (item) removeItem(item.id);
        },
    });

    // Page order is the output, so the cards themselves are draggable.
    const handleReorder = (from, to) => {
        setItems((prev) => arrayMove(prev, from, to));
        setResult(null);
    };

    const handleReset = () => {
        setItems([]);
        setSelectedItemId(null);
        setResult(null);
        setError(null);
        setProgress(null);
        setSettings(DEFAULT_CREATE_PDF_SETTINGS);
    };

    const handleCreate = async () => {
        if (!items.length || processing) return;

        recordToolUsage('image/to-pdf', { category: 'image' });

        setProcessing(true);
        setResult(null);
        setError(null);

        try {
            const files = items.map((item) => item.file);
            const blob = await createPdfFromImages(files, settings, (fileIndex, fileTotal) => {
                setProgress({ fileIndex, fileTotal });
            });

            setResult({
                name: defaultPdfName(files),
                size: blob.size,
                blob,
            });
            notifications.show({ title: t('common.success'), message: t('toPdfImage.done'), color: 'green' });
        } catch {
            const msg = t('toPdfImage.errors.failed');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setProcessing(false);
            setProgress(null);
        }
    };

    // Builds the PDF if it is not built yet, then prints it from this tab.
    const handlePrint = async () => {
        if (!items.length || printing || processing) return;
        recordToolUsage('image/to-pdf', { category: 'image' });
        setPrinting(true);
        try {
            let blob = result?.blob;
            if (!blob) {
                const files = items.map((item) => item.file);
                blob = await createPdfFromImages(files, settings);
                setResult({ name: defaultPdfName(files), size: blob.size, blob });
            }
            await printBlob(blob);
        } catch {
            const msg = t('toPdfImage.errors.failed');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setPrinting(false);
        }
    };

    const totalInputSize = items.reduce((sum, item) => sum + item.file.size, 0);

    const status = deriveToolStatus({
        hasInput: items.length > 0,
        canRun: items.length > 0,
        processing,
        hasResult: !!result,
        hasError: !!error,
    });

    const statusText = getToolStatusText(status, t, {
        empty: t('toPdfImage.previewEmpty'),
        ready: t('toPdfImage.previewReady', { count: toBnDigits(items.length) }),
        success: t('toPdfImage.done'),
    });

    const processingLabel = progress
        ? t('toPdfImage.progress', {
            file: toBnDigits(progress.fileIndex),
            files: toBnDigits(progress.fileTotal),
        })
        : t('toPdfImage.creating');

    const successLabel = result
        ? t('toPdfImage.resultMeta', {
            pages: toBnDigits(items.length),
            size: formatFileSize(result.size),
        })
        : '';

    return (
        <>
        <ToolPageLayout
            slug="image/to-pdf"
            subtitleKey="toPdfImage.subtitle"
            status={status}
            statusText={result && !processing ? successLabel : statusText}
            onReset={items.length || error ? handleReset : undefined}
            primaryAction={result && !processing ? {
                label: t('toPdfImage.download'),
                onClick: () => downloadBlob(result.blob, result.name),
                icon: IconDownload,
            } : {
                label: t('toPdfImage.create'),
                onClick: handleCreate,
                disabled: !items.length || processing,
                loading: processing,
            }}
            // Drops the PDF only — the pictures and their order stay, so another page size
            // is one click away.
            secondaryAction={[
                {
                    key: 'print',
                    label: printing ? t('toPdfImage.printing') : t('toPdfImage.print'),
                    icon: IconPrinter,
                    onClick: handlePrint,
                    disabled: !items.length || printing || processing,
                },
                ...(result && !processing ? [{
                    key: 'startOver',
                    label: t('toPdfImage.startOver'),
                    onClick: () => { setResult(null); setError(null); setProgress(null); },
                    icon: IconRefresh,
                }] : []),
            ]}
            showTrust={false}
            // The preview column tracks the settings column's height, empty
            // state included, so the panel never ends short of the settings.
            matchSidebarHeight
            // The empty drop zone, one or two pictures and the finished PDF all need
            // more room than the settings column alone gives (an A4 sheet's foot).
            matchSidebarMinHeight={result
                ? 'min(1200px, calc(100vh + 120px))'
                : (items.length <= 2 ? 'min(760px, calc(100vh - 80px))' : undefined)}
            settings={(
                <div className="flex flex-col gap-3">
                    {/* Files are picked in the preview panel, so the left column
                        carries settings only — this input is what both the empty
                        state and the grid's "+" tile open. */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={ACCEPT}
                        multiple
                        className="hidden"
                        onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                    />

                    <ToPdfImageSettings
                        settings={settings}
                        onChange={(next) => {
                            setSettings(next);
                            setResult(null);
                            setError(null);
                        }}
                    />
                </div>
            )}
            preview={(
                <ToolPreviewPanel
                    title={t('toPdfImage.previewTitle')}
                    fill
                    // A finished PDF preview and one or two pictures want the height; a grid of pages does not.
                    fillMinHeightClass={result || items.length <= 2 ? 'min-h-[min(640px,68vh)]' : undefined}
                    tall={false}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('toPdfImage.previewEmpty')}
                    idleHint={t('toPdfImage.previewEmptyHint')}
                    processingLabel={processingLabel}
                    progress={progress ? Math.round((progress.fileIndex / progress.fileTotal) * 100) : undefined}
                    error={error}
                    canvasTone="plain"
                    // The pages stay visible through a frosted sheet while the
                    // PDF is written, so the wait shows the work.
                    processingTone="veil"
                    onIdleClick={() => fileInputRef.current?.click()}
                    onIdleDrop={handleDrop}
                    onIdleDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onIdleDragLeave={() => setIsDragging(false)}
                    idleDragging={isDragging}
                    showPasteHint
                    showIdle={!items.length && !error}
                >
                    {result ? <PdfDocPreview source={result.blob} /> : null}
                    {!result && items.length > 0 && !error ? (
                        <ImageGridPreview
                            // One picture fills the panel, two sit side by side, three or more make a grid.
                            adaptive
                            files={items.map((item) => item.file)}
                            actions={processing ? [] : fileActions}
                            onItemClick={(index) => setSelectedItemId(items[index]?.id ?? null)}
                            activeIndex={selectedIndex >= 0 ? selectedIndex : undefined}
                            // Card order is page order, so the cards drag.
                            ids={items.map((item) => item.id)}
                            onReorder={processing ? undefined : handleReorder}
                            onAddMore={processing ? undefined : () => fileInputRef.current?.click()}
                            onAddDrop={processing ? undefined : handleDrop}
                            addLabel={t('toPdfImage.addMore')}
                        />
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
        {cropDialog}
        </>
    );
}
